import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, ShoppingBag, Search, Phone, Heart, ChevronLeft, ChevronRight } from 'lucide-react';
import { CONTACT_INFO, PRODUCTS } from '../constants';
import { Product } from '../types';

interface HeaderProps {
  onNavigate: (sectionId: string) => void;
  onProductSelect: (product: Product) => void;
  cartCount: number;
  wishlistCount: number;
  onCartClick: () => void;
  onWishlistClick: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
  onBack?: () => void;
  onForward?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ 
  onNavigate, 
  onProductSelect, 
  cartCount, 
  wishlistCount, 
  onCartClick,
  onWishlistClick,
  canGoBack = false,
  canGoForward = false,
  onBack,
  onForward
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isSearchOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isSearchOpen]);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setSearchResults([]);
      return;
    }

    const query = searchQuery.toLowerCase();
    const results = PRODUCTS.filter(product => 
      product.name.toLowerCase().includes(query) || 
      product.purpose.toLowerCase().includes(query) ||
      product.category.toLowerCase().includes(query)
    );
    setSearchResults(results);
  }, [searchQuery]);

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
    e.preventDefault();
    onNavigate(sectionId);
    setIsMenuOpen(false);
  };

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleProductClick = (product: Product) => {
    onProductSelect(product);
    setIsSearchOpen(false);
    setSearchQuery('');
    setIsMenuOpen(false); 
  };

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md font-heading">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center">
            <a href="#home" onClick={(e) => handleNavClick(e, 'home')} className="flex items-center">
              <img 
                src="https://res.cloudinary.com/djnukb8xv/image/upload/v1768254024/Agelife_Logo_vvwcqt.png" 
                alt="Age Life" 
                className="h-12 md:h-14 w-auto object-contain"
              />
            </a>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex space-x-8">
            <a href="#home" onClick={(e) => handleNavClick(e, 'home')} className="text-gray-600 hover:text-herbal-800 font-medium transition-colors">Home</a>
            <a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="text-gray-600 hover:text-herbal-800 font-medium transition-colors">Shop Remedies</a>
            <a href="#about" onClick={(e) => handleNavClick(e, 'about')} className="text-gray-600 hover:text-herbal-800 font-medium transition-colors">About Jamu</a>
            <a href="#locations" onClick={(e) => handleNavClick(e, 'locations')} className="text-gray-600 hover:text-herbal-800 font-medium transition-colors">Locations</a>
          </nav>

          {/* Icons & CTA */}
          <div className="flex items-center space-x-4 md:space-x-6">
            {/* History Controls */}
            <div className="hidden sm:flex items-center space-x-1 border-r border-gray-100 pr-4 mr-2">
              <button
                onClick={onBack}
                disabled={!canGoBack}
                className={`p-2 rounded-full transition-all ${
                  canGoBack 
                    ? 'text-herbal-800 hover:bg-herbal-100' 
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Back"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={onForward}
                disabled={!canGoForward}
                className={`p-2 rounded-full transition-all ${
                  canGoForward 
                    ? 'text-herbal-800 hover:bg-herbal-100' 
                    : 'text-gray-300 cursor-not-allowed'
                }`}
                title="Forward"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>

            <a 
              href={`tel:${CONTACT_INFO.phone}`} 
              className="hidden lg:flex items-center justify-center bg-herbal-100 hover:bg-herbal-800 hover:text-white text-herbal-800 w-10 h-10 rounded-full transition-all group"
              aria-label="Call Us"
            >
              <Phone className="h-5 w-5 group-hover:text-white transition-colors" />
            </a>
            
            <button 
              onClick={toggleSearch}
              className="text-gray-500 hover:text-herbal-800 transition-colors"
              aria-label="Search"
            >
              <Search className="h-6 w-6" />
            </button>

            <button 
              onClick={onWishlistClick}
              className="text-gray-500 hover:text-turmeric-600 transition-colors relative"
              aria-label="Open Wishlist"
            >
              <Heart className="h-6 w-6" />
              <span className={`absolute -top-1 -right-1 bg-turmeric-500 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center transition-transform ${wishlistCount > 0 ? 'scale-100' : 'scale-0'}`}>
                {wishlistCount}
              </span>
            </button>
            
            <button 
              onClick={onCartClick}
              className="text-gray-500 hover:text-herbal-800 transition-colors relative"
              aria-label="Open Cart"
            >
              <ShoppingBag className="h-6 w-6" />
              <span className={`absolute -top-1 -right-1 bg-herbal-800 text-white text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center transition-transform ${cartCount > 0 ? 'scale-100' : 'scale-0'}`}>
                {cartCount}
              </span>
            </button>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="text-gray-500 hover:text-herbal-800 focus:outline-none ml-2"
              >
                {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Search Overlay */}
        {isSearchOpen && (
          <div className="absolute top-0 left-0 w-full bg-white shadow-lg z-50 animate-fade-in border-b border-gray-100 min-h-[80px]">
            <div className="container mx-auto px-4 py-4">
              <div className="flex items-center gap-4">
                <Search className="h-5 w-5 text-gray-400 flex-shrink-0" />
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search remedies by name, purpose, or category..."
                  className="flex-1 outline-none text-lg text-gray-800 placeholder-gray-400 bg-transparent h-12"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <button onClick={toggleSearch} className="text-gray-500 hover:text-red-500 p-2">
                  <X className="h-6 w-6" />
                </button>
              </div>
              
              {/* Search Results */}
              {searchQuery && (
                <div className="mt-2 max-h-[70vh] overflow-y-auto custom-scrollbar border-t border-gray-100 pt-4 pb-4">
                  {searchResults.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {searchResults.map(product => (
                        <div 
                          key={product.id}
                          onClick={() => handleProductClick(product)}
                          className="flex items-center p-3 hover:bg-earth-50 rounded-lg cursor-pointer transition-colors group border border-transparent hover:border-gray-100"
                        >
                          <img 
                            src={product.image} 
                            alt={product.name} 
                            className="w-16 h-16 object-contain rounded-md bg-white border border-gray-100 p-1" 
                          />
                          <div className="ml-4">
                            <h4 className="font-bold text-herbal-900 group-hover:text-turmeric-600 transition-colors">
                              {product.name}
                            </h4>
                            <p className="text-xs text-gray-500 line-clamp-1">
                              {product.purpose}
                            </p>
                            <span className="text-[10px] uppercase font-bold text-turmeric-600 mt-1 inline-block">
                              {product.category}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="py-8 text-center text-gray-500">
                      <p>No remedies found for "{searchQuery}"</p>
                      <p className="text-sm mt-2">Try searching for symptoms like "pain", "fever", or "detox".</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && !isSearchOpen && (
        <div className="md:hidden bg-earth-50 border-t border-gray-100 animate-fade-in">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <a href="#home" onClick={(e) => handleNavClick(e, 'home')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-herbal-800 hover:bg-herbal-100">Home</a>
            <a href="#products" onClick={(e) => handleNavClick(e, 'products')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-herbal-800 hover:bg-herbal-100">Shop Remedies</a>
            <a href="#about" onClick={(e) => handleNavClick(e, 'about')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-herbal-800 hover:bg-herbal-100">About Jamu</a>
            <a href="#locations" onClick={(e) => handleNavClick(e, 'locations')} className="block px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-herbal-800 hover:bg-herbal-100">Locations</a>
            <a href={`tel:${CONTACT_INFO.phone}`} className="block px-3 py-2 text-base font-bold text-herbal-800 bg-herbal-100 rounded-md mt-4 text-center flex items-center justify-center gap-2">
              <Phone className="h-4 w-4" />
              Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
};